import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
    performRegister as performRegisterAction,
    setRegisterInput,
} from '../../../Actions/Register.action';
import { useTypedSelector } from '../../../Hooks/useTypedSelector';
import { translate } from '../../../Services/translation';
import Button from '../../Button';
import Input from '../../Inputs/Input';
import Checkbox from '../../Inputs/Checkbox';
import { StyledRegister, StyledCheckoutDropdown } from '../styles/RegisterForm.styles';
import RegisterProps, { B2B_CUSTOMER_TYPE, B2C_CUSTOMER_TYPE, CustomerType } from '../types/RegisterProps';
import { getColor } from '../../../styling/mixins/colors';
import { fontFamilyBold, text } from '../../../styling/mixins/typography';
import { fontSizeText } from '../../../styling/variables/typography';
import VippsButton from '../../VippsLogin/VippsButton';
import { TextSeparator } from '../../TextSeparator';
import { VIPPS_BUTTON_TYPE } from '../../../constants';
declare var LitiumConstants: any;

const StyledRegisterForm = styled(StyledRegister)`
  .password-match-error {
    ${text('medium')}
    color: ${getColor('alert')};
    margin-bottom: 1.6rem;
  }
`;

const RegisterForm = (props: RegisterProps) => {
    const { isLoading, payload } = useTypedSelector((state) => state.register);
    const dispatch = useDispatch();
    const [matchingPasswordError, setMatchingPasswordError] = useState<boolean>(false);
    const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType>(B2C_CUSTOMER_TYPE)

    const performRegister = (e?: any) => {
        if (e) {
            e.preventDefault();
        }

        if (selectedCustomerType === B2C_CUSTOMER_TYPE && payload.registerInfo.password != payload.registerInfo.repeatpassword) {
            setMatchingPasswordError(true);
            document.querySelector("input[type='password']")?.scrollIntoView();
            return;
        }

        dispatch(performRegisterAction(payload.registerInfo, selectedCustomerType));
    };

    const handleInputChange = (key: string, value: any) => {
        dispatch(setRegisterInput(key, value));
    };

    if (!payload?.registerInfo?.country) {
        dispatch(setRegisterInput('country', LitiumConstants.countries[0].value));
    }

    return (
        <StyledRegisterForm>
            <h1>{props.title}</h1>
            <div dangerouslySetInnerHTML={{ __html: props.text }}></div>

            <CustomerTypeSelector
                selectedCustomerType={selectedCustomerType}
                onClick={(v: string) => setSelectedCustomerType(v)}
            />


            <form className="form" onSubmit={performRegister} autoComplete="off">

                {selectedCustomerType === B2C_CUSTOMER_TYPE ? (
                    <>
                        <VippsButton type={VIPPS_BUTTON_TYPE.Register} />
                        <TextSeparator textToShow={translate('general.separator-text')} bgColor={getColor("grey", 100)} />
                        <RegisterB2CForm
                            isLoading={isLoading}
                            onInputChange={handleInputChange}
                            matchingPasswordError={matchingPasswordError}
                            payload={payload}
                        />
                    </>
                ) : (
                    <RegisterB2BForm
                        isLoading={isLoading}
                        onInputChange={handleInputChange}
                        payload={payload}
                    />
                )}

                <Button
                    btnSize="large"
                    btnType="primary"
                    fullwidth={true}
                    type="submit"
                    className="submit"
                    disabled={isLoading}
                >
                    {!isLoading && translate('register.contact.info.submit')}
                    {isLoading && translate('general.loading')}
                </Button>
            </form>
        </StyledRegisterForm>
    );
};

export default RegisterForm;

const StyledCustomerTypeSelector = styled.div`

  .customertype-selector-container {
    margin: 2rem 0;
    display: flex;
    align-items: center;
    gap: 1rem;

    .item {
        color: ${getColor('gray', 600)};
        font-size: ${fontSizeText['small']};

        &:hover {
            cursor: pointer;
        }
    }

    .selected {
        ${fontFamilyBold};
    }
  }
`;

interface ICustomerTypeSelectorProps {
    selectedCustomerType: CustomerType,
    onClick: (v: string) => void
}

function CustomerTypeSelector(props: ICustomerTypeSelectorProps) {

    return (
        <StyledCustomerTypeSelector>
            <div className="customertype-selector-container">
                <CustomerTypeComponent
                    text={translate('register.selector.b2c')}
                    isSelected={props.selectedCustomerType === B2C_CUSTOMER_TYPE}
                    onClick={() => props.onClick(B2C_CUSTOMER_TYPE)}
                />
                <span className='item'>|</span>
                <CustomerTypeComponent
                    text={translate('register.selector.b2b')}
                    isSelected={props.selectedCustomerType === B2B_CUSTOMER_TYPE}
                    onClick={() => props.onClick(B2B_CUSTOMER_TYPE)}
                />
            </div>
        </StyledCustomerTypeSelector>
    )
}

interface ICustomerTypeProps {
    text: string,
    isSelected: boolean,
    onClick: () => void
}

function CustomerTypeComponent(props: ICustomerTypeProps) {

    const className = props.isSelected ? "selected" : ""

    return (
        <div className={`item ${className}`} onClick={props.onClick}>{props.text}</div>
    )
}

function RegisterB2CForm({ isLoading, onInputChange, matchingPasswordError, payload }) {
    return (
        <>
            <ContactInformation isLoading={isLoading} onInputChange={onInputChange} matchingPasswordError={matchingPasswordError} withPassword={true} />
            <DeliveryAddress isLoading={isLoading} onInputChange={onInputChange} payload={payload} />

            <Checkbox
                inputSize="large"
                id="accept-news"
                checked={payload.registerInfo.acceptNews == 'true'}
                onChange={(e) => onInputChange('acceptNews', e.target.checked.toString())
                }
                inputState='default'
                label={translate(`register.accept.news`)}
                wrapperClassName="news-checkbox"
            />
        </>
    );
}

function RegisterB2BForm({ isLoading, onInputChange, payload }) {
    return (
        <>
            <OrganizationForm isLoading={isLoading} onInputChange={onInputChange} />
            <ContactInformation isLoading={isLoading} onInputChange={onInputChange} />
            <DeliveryAddress isLoading={isLoading} onInputChange={onInputChange} payload={payload} />
        </>
    );
}

function OrganizationForm({ isLoading, onInputChange }) {

    return (
        <>
            <h4>{translate('register.company.info.header')}</h4>

            <InputField keyName="company" autoComplete='organization' isLoading={isLoading} onInputChange={onInputChange} />
            <InputField keyName="organizationnumber" isLoading={isLoading} onInputChange={onInputChange} />
            <InputField keyName="billingemail" autoComplete='email' type='email' isLoading={isLoading} onInputChange={onInputChange} />
        </>
    )
}

function ContactInformation({ isLoading, onInputChange, matchingPasswordError = false, withPassword = false }) {
    return (
        <>
            <h4>{translate('register.contact.info.header')}</h4>

            <InputField keyName="firstname" mandatory isLoading={isLoading} onInputChange={onInputChange} />
            <InputField keyName="lastname" mandatory isLoading={isLoading} onInputChange={onInputChange} />
            <InputField keyName="phone" autoComplete='tel' type='tel' mandatory isLoading={isLoading} onInputChange={onInputChange} />
            <InputField keyName="email" autoComplete='email' type="email" mandatory isLoading={isLoading} onInputChange={onInputChange} />

            {withPassword && (
                <>
                    <InputField keyName="password" mandatory isLoading={isLoading} type="password" onInputChange={onInputChange} />
                    <InputField keyName="repeatpassword" mandatory isLoading={isLoading} type="password" onInputChange={onInputChange} />

                    {matchingPasswordError && (
                        <div className="password-match-error">{translate('changepassword.passwordconfirmationdoesnotmatch')}</div>
                    )}
                </>

            )}
        </>
    )
}

function DeliveryAddress({ isLoading, onInputChange, payload }) {
    return (
        <>
            <h4>{translate("register.delivery.info.header")}</h4>
            <InputField keyName="careof" isLoading={isLoading} onInputChange={onInputChange} />
            <InputField keyName="address" autoComplete='street-address' mandatory isLoading={isLoading} onInputChange={onInputChange} />
            <InputField keyName="zipcode" autoComplete='postal-code' mandatory isLoading={isLoading} onInputChange={onInputChange} />
            <InputField keyName="city" autoComplete='address-level2' mandatory isLoading={isLoading} onInputChange={onInputChange} />

            <div className="country-select-container">
                <label className="country-label">
                    {translate(`register.contact.info.country`)}*
                </label>
                <StyledCheckoutDropdown
                    placeholder={translate(`register.contact.info.country`)}
                    selectedValue={payload.registerInfo.country || LitiumConstants.countries[0].value}
                    options={LitiumConstants.countries.map((country) => ({
                        value: country.value,
                        label: country.text,
                    }))}
                    disabled={LitiumConstants.countries.length <= 1}
                />
            </div>
        </>
    )
}

function InputField({ keyName, mandatory = false, autoComplete = '', type = 'text', isLoading, onInputChange }) {
    return (
        <Input
            label={`${translate(`register.contact.info.${keyName}`)}`}
            inputSize="large"
            wrapperClassName="input__wrapper"
            placeholder={`${translate(`register.contact.info.${keyName}`)}`}
            onChange={(e) => onInputChange(keyName, e.target.value)}
            disabled={isLoading}
            type={type}
            required={mandatory}
            autoComplete={autoComplete}
        />
    );
}